import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import AppContext from "../../context/AppContext";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "../../utils/toast";
import { GetTaskHistory } from "../../service/Task";
import { fetchTaskDetail, updateTaskDetails } from "../../service/Task";
import { FaArrowLeft, FaUserEdit, FaFileAlt, FaExternalLinkAlt } from "react-icons/fa";
import translations from "../../components/common/Translations";
import debounce from "lodash.debounce";
import Modal from "./Modal";
import { HiOutlineArrowLongLeft } from "react-icons/hi2";
import CommonButton from "../../components/common/CommonButton";
import { getPermissionByScreenCode } from "../../utils/permissions";
import ModalCloseButton from "../../components/common/ModalCloseButton";
import AttachmentList from "../../components/common/AttachmentList";
import { findAttachment } from "../../utils/attachments";

const getBackendErrorMessage = (error) =>
  error?.response?.data?.body?.value ??
  error?.response?.data?.body?.error ??
  error?.response?.data?.message ??
  error?.body?.value ??
  error?.body?.error ??
  error?.message;

const TaskDetailPage = ({ taskId: propTaskId, accountNo: propAccountNo, onClose }) => {
  const { taskId: paramTaskId } = useParams();
  const taskId = propTaskId || paramTaskId;
  const isSlider = typeof onClose === "function";
  const [loading, setLoading] = useState(true);
  const [fieldData, setFieldData] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [taskHistory, setTaskHistory] = useState([]);
  const [page, setPage] = useState(0);
  const [task, setTask] = useState(null);
  const [size] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [expandedTask, setExpandedTask] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [taskDetails, setTaskDetails] = useState({});
  const context = useContext(AppContext);
  const { accountNo, theme, langMode, userType, setKey, setSelectedUserName, setSelectedUserId, setAccountNo } = context;
  const canUpdateTask = Boolean(getPermissionByScreenCode("TASK_MANAGEMENT")?.canUpdate);
  const [open, setOpen] = useState(false)
  const [img, setImg] = useState('')


  const getSafeTaskDetails = (taskDetails) => {
    try {
      if (typeof taskDetails === "object" && taskDetails !== null) {
        return taskDetails;
      }
      if (typeof taskDetails === "string") {
        return JSON.parse(taskDetails);
      }
    } catch (error) {
      console.warn("Invalid taskDetails format:", taskDetails);
    }
    return {};
  };
  const handleOpenModal = (img) => {
    setOpen(true)
    setImg(img)
  }
  useEffect(() => {
    fetchTaskDetails();
  }, [taskId]);

  const formattedDate = (dateString) => {
    if (!dateString) {
      return "-";
    }
    const date = new Date(dateString);
    const options = {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true, // Use 12-hour format with AM/PM
    };

    const timeZone = dateString.match(/([+\-]\d{2}:\d{2})$/)?.[1] || 'UTC';
    options.timeZone = timeZone;
    return date.toLocaleString('en-US', options);
  };
  const fetchTaskDetails = async () => {
    try {
      const data = await fetchTaskDetail(taskId);
      const task = data.body.value.task;
      const taskDetail = getSafeTaskDetails(task.taskDetailsJson);
      setTaskDetails(taskDetail);
      const additionalFields = {
        TaskID: task.taskId,
        "Account No.": task.accountNo,
        Activity: task.taskDetailsJson.Activity,
      };
      const otherFields = {
        Priority: task.priority,
        Status: task.taskStatus,
        "Created Date": task.createdDate,
        Latitude: task.latitude,
        Longitude: task.longitude,
        "Area Code": task.areaCode,
        Tariff: task.tariff,
        Office: task.office,
        Area: task.area,
      };
      const fieldValues = [
        ...Object.entries(additionalFields).map(([fieldName, value]) => ({
          fieldName,
          value,
        })),
        ...Object.entries(otherFields).map(([fieldName, value]) => ({
          fieldName,
          value,
        })),
        ...Object.entries(taskDetails).map(([fieldName, value]) => ({
          fieldName,
          value,
        })),
      ];
      setTask(task);
      setFieldData(fieldValues);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching task details:", error);
      setLoading(false);
    }
  };

  const navigate = useNavigate();

  const activeAccountNo = task?.accountNo || propAccountNo;

  const fetchTaskHistory = useCallback(async () => {
    if (!activeAccountNo || !hasMore) return; // Prevent fetching if there are no more results

    setIsLoading(true);
    try {
      const responseData = await GetTaskHistory(activeAccountNo, page, size);

      if (responseData === 'No Data Found') {
        setHasMore(false); // Stop further API calls
        setIsLoading(false);
        toast.error(translations[langMode].nomoredatatoload)
        return; 
      }

      if (!Array.isArray(responseData)) {
        throw new Error("Response data is not an array");
      }

      setTaskHistory((prevHistory) => {
        const existingTaskIds = new Set(prevHistory.map((task) => task.taskHistoryId));
        const uniqueTasks = responseData
          .filter((task) => !existingTaskIds.has(task.taskHistoryId))
          // Remember the page each entry came from so an attachment download
          // can refetch just that page for a fresh presigned link.
          .map((task) => ({ ...task, historyPage: page }));
        return [...prevHistory, ...uniqueTasks];
      });

    } catch (error) {
      console.error("Error fetching task history:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [activeAccountNo, page, size, hasMore]);


  useEffect(() => {
    fetchTaskHistory();
  }, [fetchTaskHistory]);

  const loadMore = () => {
    if (!isLoading && hasMore) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  const handleScroll = useCallback(
    debounce(() => {
      if (
        window.innerHeight + document.documentElement.scrollTop >=
        document.documentElement.offsetHeight - 200
      ) {
        loadMore();
      }
    }, 300),
    [isLoading, hasMore]
  );

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => { }, [taskHistory]);

  // Every history entry of a task repeats that task's attachments, so they
  // are shown only on the first entry listed for each task.
  const attachmentEntryIndexes = useMemo(() => {
    const seenTaskIds = new Set();
    const indexes = new Set();
    taskHistory.forEach((entry, index) => {
      if (!entry?.attachments?.length) return;
      if (entry.taskId != null) {
        if (seenTaskIds.has(entry.taskId)) return;
        seenTaskIds.add(entry.taskId);
      }
      indexes.add(index);
    });
    return indexes;
  }, [taskHistory]);

  // downloadUrl expires after ~60 minutes, so refetch the entry's page and
  // take the matching attachment's current link instead of the stored one.
  const resolveFreshHistoryAttachment = (entry) => async (attachment) => {
    const freshPage = await GetTaskHistory(activeAccountNo, entry.historyPage ?? 0, size);
    if (!Array.isArray(freshPage)) return null;
    const sameTask = freshPage.filter((h) =>
      entry.taskId != null ? h.taskId === entry.taskId : h.taskHistoryId === entry.taskHistoryId
    );
    return findAttachment(sameTask.flatMap((h) => h.attachments || []), attachment);
  };

  const handleConfirm = async () => {
    try {
      console.log("Current fieldData:", fieldData);
      const updatedTaskDetails = fieldData.reduce((acc, field) => {
        acc[field.fieldName] = field.value;
        return acc;
      }, {});
      updatedTaskDetails["Activity"] =
        taskDetails?.Activity || "Default_Activity";

      updatedTaskDetails["Priority"] = task.priority || "Low_Priority";
      const updatedTask = {
        taskId: task.taskId,
        workflowId: task.workflowId,
        assigneeId: task.assigneeId,
        priority: task.priority || "Low_Priority",
        taskDetailsJson: getSafeTaskDetails(updatedTaskDetails),
        taskStatus: task.taskStatus,
        assignedBy: task.assignedBy,
        createdDate: task.createdDate,
        latitude: task.longitude,
        longitude: task.latitude,
        areaCode: task.areaCode,
        tariff: task.tariff,
        accountNo: task.accountNo,
        createdBy: task.createdBy,
        finishedDate: task.finishedDate,
        office: task.office,
        area: task.area,
        source: task.source,
        icon: task.icon,
        completed: task.completed,
        assigned: task.assigned,
        deleted: task.deleted,
      };
      const data = await updateTaskDetails(updatedTask);
      if (data.header.code === 600) {
        toast.success(translations[langMode].taskdetailupdatedsuc);
        setEditMode(false);
      } else {
        const backendMessage =
          data?.body?.value ??
          data?.body?.error ??
          data?.header?.message;
        if (backendMessage) {
          toast.error(backendMessage);
        }
      }
    } catch (error) {
      const backendMessage = getBackendErrorMessage(error);
      if (backendMessage) {
        toast.error(backendMessage);
      }
    }
  };
  const handleEditClick = () => {
    if (!canUpdateTask) return;
    setEditMode(!editMode);
  };

  const handleDiscard = () => {
    fetchTaskDetails();
    setEditMode(false);
  };
  const handleToggle = (index) => {
    setExpandedTask(expandedTask === index ? null : index);
  };
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return date.toLocaleDateString(undefined, options);
  };
  const isValidURL = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const humanizeKey = (key) =>
    String(key)
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const parseBodyObject = (body) => {
    if (typeof body !== "string") return null;
    const trimmed = body.trim();
    if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return null;

    // Try strict JSON first (quoted keys/values).
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const entries = Object.entries(parsed).map(([key, value]) => ({
          key,
          value: value === null || value === undefined ? "" : String(value),
        }));
        return entries.length ? entries : null;
      }
    } catch (_) {
      // fall through to key=value parsing
    }

    // Fallback: Java map toString format -> {key=value, key=value}
    const inner = trimmed.slice(1, -1).trim();
    if (!inner) return null;
    const entries = inner.split(",").map((pair) => {
      const eq = pair.indexOf("=");
      if (eq === -1) return null;
      return {
        key: pair.slice(0, eq).trim(),
        value: pair.slice(eq + 1).trim(),
      };
    });
    if (entries.some((entry) => entry === null || !entry.key)) return null;
    return entries.length ? entries : null;
  };

  // True when the URL points to an image (ignoring any query/hash suffix).
  const isImageUrl = (url) => {
    if (typeof url !== "string") return false;
    const clean = url.split("?")[0].split("#")[0];
    return /\.(jpe?g|png|gif|webp|bmp|svg|heic|heif)$/i.test(clean);
  };

  // Human-friendly file name from a URL, falling back to "Attachment N".
  const getAttachmentName = (url, index) => {
    const fallback = `${translations[langMode].attachments ?? "Attachment"} ${index + 1}`;
    if (typeof url !== "string") return fallback;
    try {
      const clean = url.split("?")[0].split("#")[0];
      const last = clean.substring(clean.lastIndexOf("/") + 1);
      const decoded = decodeURIComponent(last);
      return decoded || fallback;
    } catch (_) {
      return fallback;
    }
  };

  // attachmentUrls may arrive as an array, a JSON array string, or a comma-separated string,
  // on either the task object or inside taskDetailsJson.
  const getAttachmentUrls = () => {
    const raw =
      task?.attachmentUrls ??
      taskDetails?.attachmentUrls ??
      taskDetails?.["attachmentUrls"];
    if (!raw) return [];
    if (Array.isArray(raw)) return raw.filter(Boolean);
    if (typeof raw === "string") {
      const trimmed = raw.trim();
      if (!trimmed) return [];
      if (trimmed.startsWith("[")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) return parsed.filter(Boolean);
        } catch (_) {
          // fall through to comma-split
        }
      }
      return trimmed.split(",").map((s) => s.trim()).filter(Boolean);
    }
    return [];
  };

  const handleGoBack = () => {
    setKey((prevKey) => prevKey + 1); // Update the key to force re-render
    sessionStorage.setItem("resetSearch", "true");
    navigate("/task-manage"); // Or the route to go back
    setSelectedUserId('')
    setSelectedUserName("Default")
    setAccountNo("")
  };

  const handleAccountNoChange = (e) => {
    const newAccountNo = e.target.value;
    setTask((prev) => ({ ...prev, accountNo: newAccountNo }));
    setFieldData((prev) =>
      prev.map((field) =>
        field.fieldName === "Account No."
          ? { ...field, value: newAccountNo }
          : field
      )
    );
  };
  const handleLatitudeChange = (e) => {
    const newLatitude = e.target.value;
    setTask((prev) => ({ ...prev, latitude: newLatitude }));
    setFieldData((prev) =>
      prev.map((field) =>
        field.fieldName === "Latitude"
          ? { ...field, value: newLatitude }
          : field
      )
    );
  };

  const handlePriorityChange = (e) => {
    const newPriority = e.target.value;
    setTask((prev) => ({ ...prev, priority: newPriority }));
    setFieldData((prev) =>
      prev.map((field) =>
        field.fieldName === "Priority"
          ? { ...field, value: newPriority }
          : field
      )
    );
  };
  const handleAreacodeChange = (e) => {
    const newAreaCode = e.target.value;
    setTask((prev) => ({ ...prev, areaCode: newAreaCode }));
    setFieldData((prev) =>
      prev.map((field) =>
        field.fieldName === "Area Code"
          ? { ...field, value: newAreaCode }
          : field
      )
    );
  };

  const handleTarrifChange = (e) => {
    const newtariff = e.target.value;
    setTask((prev) => ({ ...prev, tariff: newtariff }));
    setFieldData((prev) =>
      prev.map((field) =>
        field.fieldName === "Tariff" ? { ...field, value: newtariff } : field
      )
    );
  };

  const handleOfficeChange = (e) => {
    const newoffice = e.target.value;
    setTask((prev) => ({ ...prev, office: newoffice }));
    setFieldData((prev) =>
      prev.map((field) =>
        field.fieldName === "Office" ? { ...field, value: newoffice } : field
      )
    );
  };
  const handleAreaChange = (e) => {
    const newarea = e.target.value;
    setTask((prev) => ({ ...prev, area: newarea }));
    setFieldData((prev) =>
      prev.map((field) =>
        field.fieldName === "Area" ? { ...field, value: newarea } : field
      )
    );
  };
  const handleLongitudeChange = (e) => {
    const newLongitude = e.target.value;
    setTask((prev) => ({ ...prev, longitude: newLongitude }));
    setFieldData((prev) =>
      prev.map((field) =>
        field.fieldName === "Longitude"
          ? { ...field, value: newLongitude }
          : field
      )
    );
  };

  const getSafeRemarksValue = (details) => {
    if (!details || typeof details !== "object") return "";

    const directCandidates = ["Remarks", "Remark", "remarks", "remark"];
    for (const key of directCandidates) {
      const value = details?.[key];
      if (value !== undefined && value !== null && String(value).trim() !== "") {
        return String(value);
      }
    }

    const canonicalize = (value) =>
      String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

    const targetCanonicals = new Set(["remarks", "remark"]);
    const matchedKey = Object.keys(details).find((key) =>
      targetCanonicals.has(canonicalize(key))
    );

    if (!matchedKey) return "";
    const matchedValue = details?.[matchedKey];
    return matchedValue !== undefined && matchedValue !== null ? String(matchedValue) : "";
  };

  const remarksValue = getSafeRemarksValue(taskDetails);
  return (
    <div className="w-full bg-slate-50 min-h-full">
      <div className="flex items-center justify-between px-5 pt-5 pb-2">
        <div className="flex items-center gap-3">
          <button
            onClick={isSlider ? onClose : handleGoBack}
            className="
                        rounded-lg
                        bg-blue-50
                        text-[#236DB4]
                        hover:bg-blue-100
                        focus:outline-none
                        focus:ring-2
                        focus:ring-[#236DB4]/30
                        transition
                        duration-200
                        ease-in-out
                        flex items-center justify-center
                        min-h-[36px] h-9 w-9
                        "
            aria-label="Go back"
          >
            <HiOutlineArrowLongLeft size={20} />
          </button>
          <div>
            <h4
              className={`text-lg font-bold text-slate-800 ${theme === "dark"
                ? "dark-mode "
                : theme === "high-contrast"
                  ? "high-contrast  "
                  : ""
                }`}
            >
              {translations[langMode].taskdetail}
            </h4>
            {isSlider && (
              <p className="text-xs text-slate-500 mt-0.5">
                Task {taskId} - Account {propAccountNo || task?.accountNo || ""}
              </p>
            )}
          </div>
        </div>
        {isSlider && (
          <ModalCloseButton onClick={onClose} ariaLabel="Close" />
        )}
      </div>
      <div
        className={`px-5 py-5 ${theme === "dark"
          ? "dark-mode"
          : theme === "high-contrast"
            ? "high-contrast"
            : ""
          }`}
      >
        <div className={`flex ${isSlider ? "lg:flex-row" : "xsm:flex-row"} flex-col gap-4`}>
          <div
            className={`${isSlider ? "lg:w-1/2" : "xsm:w-[700px]"} w-full  ${theme === "dark"
              ? "dark-mode"
              : theme === "high-contrast"
                ? "high-contrast"
                : ""
              }`}
          >
            <div className=" py-2 flex justify-between items-center">
              <h3
                className={`${theme === "dark"
                  ? "dark-mode "
                  : theme === "high-contrast"
                    ? "high-contrast  "
                    : ""
                  } text-sm font-semibold text-slate-700`}
              >
                {translations[langMode].taskdetail}
              </h3>
              {userType !== "field_agent" && (
                <span className="inline-flex items-center gap-1">
                  <FaUserEdit
                    onClick={handleEditClick}
                    title={translations[langMode]?.updatetaskdetail ?? "Update task details"}
                    className={`h-7 w-7 p-1.5 rounded-md ${!canUpdateTask
                      ? "text-slate-300 cursor-not-allowed"
                      : `cursor-pointer hover:bg-slate-100 ${editMode ? "text-emerald-600 bg-emerald-50" : "text-slate-500"}`
                      } ${theme === "dark"
                        ? "dark-mode "
                        : theme === "high-contrast"
                          ? "high-contrast  "
                          : ""
                      }`}
                  />
                </span>
              )}
            </div>
            <div
              className={`rounded-xl border border-slate-200 border-l-4 border-l-[#236DB4] bg-white p-5 ${theme === "dark"
                ? "dark-mode"
                : theme === "high-contrast"
                  ? "high-contrast"
                  : ""
                }`}
            >
              <dl className="divide-y divide-slate-100">
                {loading ? (
                  <div className="text-center text-slate-400">{translations[langMode].Loading}</div>
                ) : task === null ? (
                  <div className="text-center text-xl mt-10 text-red-500">
                    {translations[langMode].taskNotFound || "Task details could not be loaded."}
                  </div>
                ) : (
                  <>
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4" title="Unique identifier generated for this task">
                      <dt
                        className={`text-sm font-semibold text-slate-700 ${theme === "dark"
                          ? "dark-mode "
                          : theme === "high-contrast"
                            ? "high-contrast  "
                            : ""
                          }`}
                      >
                        {translations[langMode].taskId}
                      </dt>
                      <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                        {editMode ? (
                          <input
                            type="text"
                            className={`w-full border bg-slate-100 border-slate-200 focus:border-[#236DB4]
                               focus:ring-2 focus:ring-[#236DB4]/20 rounded-md p-2 ${theme === "dark"
                                ? "dark-mode "
                                : theme === "high-contrast"
                                  ? "high-contrast  "
                                  : ""
                              }`}
                            value={task.taskId}
                            disabled
                          />
                        ) : (
                          <span
                            className={`inline-block rounded-md px-3 py-1 text-slate-700 ${theme === "dark"
                              ? "dark-mode "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {task.taskId}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4" title="Account number associated with this task">
                      <dt
                        className={`text-sm font-semibold text-slate-700 ${theme === "dark"
                          ? "dark-mode "
                          : theme === "high-contrast"
                            ? "high-contrast  "
                            : ""
                          }`}
                      >
                        {translations[langMode].accountNo}
                      </dt>
                      <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                        {editMode ? (
                          <input
                            type="text"
                            className={`w-full bg-slate-100 border border-slate-200 focus:border-[#236DB4]
                               focus:ring-2 focus:ring-[#236DB4]/20 rounded-md p-2 ${theme === "dark"
                                ? "dark-mode "
                                : theme === "high-contrast"
                                  ? "high-contrast  "
                                  : ""
                              }`}
                            value={task.accountNo}
                            onChange={handleAccountNoChange}
                            disabled
                          />
                        ) : (
                          <span
                            className={`inline-block rounded-md px-3 py-1 text-slate-700 ${theme === "dark"
                              ? "dark-mode "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {task.accountNo}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4" title="The specific activity or workflow action to be performed">
                      <dt
                        className={`text-sm font-semibold text-slate-700 ${theme === "dark"
                          ? "dark-mode "
                          : theme === "high-contrast"
                            ? "high-contrast  "
                            : ""
                          }`}
                      >
                        {translations[langMode].Activity}
                      </dt>
                      <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                        {editMode ? (
                          <input
                            type="text"
                            className={`w-full bg-slate-100 border border-slate-200 focus:border-[#236DB4]
                               focus:ring-2 focus:ring-[#236DB4]/20 rounded-md p-2 ${theme === "dark"
                                ? "dark-mode "
                                : theme === "high-contrast"
                                  ? "high-contrast  "
                                  : ""
                              }`}
                            value={taskDetails.Activity}
                            onChange={(e) =>
                              setTask({
                                ...task,
                                taskDetails: {
                                  ...taskDetails,
                                  Activity: e.target.value,
                                },
                              })
                            }
                            disabled
                          />
                        ) : (
                          <span
                            className={`inline-block rounded-md px-3 py-1 text-slate-700 ${theme === "dark"
                              ? "dark-mode "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {taskDetails.Activity}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4" title="Urgency level of the task (e.g. Low, Medium, High)">
                      <dt
                        className={`text-sm font-semibold text-slate-700 ${theme === "dark"
                          ? "dark-mode "
                          : theme === "high-contrast"
                            ? "high-contrast  "
                            : ""
                          }`}
                      >
                        {translations[langMode].priority}
                      </dt>
                      <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                        {editMode ? (
                          <input
                            type="text"
                            className={`w-full border bg-slate-100 border-slate-200 focus:border-[#236DB4]
                               focus:ring-2 focus:ring-[#236DB4]/20 rounded-md p-2 ${theme === "dark"
                                ? "dark-mode "
                                : theme === "high-contrast"
                                  ? "high-contrast  "
                                  : ""
                              }`}
                            value={task.priority}
                            // onChange={handlePriorityChange}
                            disabled
                          />
                        ) : (
                          <span
                            className={`inline-block rounded-md px-3 py-1 text-slate-700 ${theme === "dark"
                              ? "dark-mode "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {task.priority}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4" title="Current progress status of the task">
                      <dt
                        className={`text-sm font-semibold text-slate-700 ${theme === "dark"
                          ? "dark-mode "
                          : theme === "high-contrast"
                            ? "high-contrast  "
                            : ""
                          }`}
                      >
                        {translations[langMode].status}
                      </dt>
                      <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                        {editMode ? (
                          <input
                            type="text"
                            className={`w-full bg-slate-100 border border-slate-200 focus:border-[#236DB4]
                               focus:ring-2 focus:ring-[#236DB4]/20 rounded-md p-2 ${theme === "dark"
                                ? "dark-mode "
                                : theme === "high-contrast"
                                  ? "high-contrast  "
                                  : ""
                              }`}
                            value={task.taskStatus}
                            onChange={(e) =>
                              setTask({ ...task, taskStatus: e.target.value })
                            }
                            disabled
                          />
                        ) : (
                          <span
                            className={`inline-block rounded-md px-3 py-1 text-slate-700 ${theme === "dark"
                              ? "dark-mode "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {task.taskStatus}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4" title="Date and time when the task was initially created">
                      <dt
                        className={`text-sm font-semibold text-slate-700 ${theme === "dark"
                          ? "dark-mode "
                          : theme === "high-contrast"
                            ? "high-contrast  "
                            : ""
                          }`}
                      >
                        {translations[langMode].cretDate}
                      </dt>
                      <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                        {editMode ? (
                          <input
                            type="text"
                            className={`w-full bg-slate-100 border border-slate-200 focus:border-[#236DB4]
                               focus:ring-2 focus:ring-[#236DB4]/20 rounded-md p-2 ${theme === "dark"
                                ? "dark-mode "
                                : theme === "high-contrast"
                                  ? "high-contrast  "
                                  : ""
                              }`}
                            value={formattedDate(task.createdDate)}
                            onChange={(e) =>
                              setTask({
                                ...task,
                                createdDate: e.target.value,
                              })
                            }
                            disabled
                          />
                        ) : (
                          <span
                            className={`inline-block rounded-md px-3 py-1 text-slate-700 ${theme === "dark"
                              ? "dark-mode "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {formattedDate(task.createdDate)}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4" title="Geographical latitude coordinate for the task location">
                      <dt
                        className={`text-sm font-semibold text-slate-700 ${theme === "dark"
                          ? "dark-mode "
                          : theme === "high-contrast"
                            ? "high-contrast  "
                            : ""
                          }`}
                      >
                        {translations[langMode].latitude}
                      </dt>
                      <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                        {editMode ? (
                          <input
                            type="text"
                            className={`w-full border border-slate-200 focus:border-[#236DB4]
                               focus:ring-2 focus:ring-[#236DB4]/20 rounded-md p-2 ${theme === "dark"
                                ? "dark-mode "
                                : theme === "high-contrast"
                                  ? "high-contrast  "
                                  : ""
                              }`}
                            value={task.latitude}
                            onChange={handleLatitudeChange}
                          />
                        ) : (
                          <span
                            className={`inline-block rounded-md px-3 py-1 text-slate-700 ${theme === "dark"
                              ? "dark-mode "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {task.latitude}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4" title="Geographical longitude coordinate for the task location">
                      <dt
                        className={`text-sm font-semibold text-slate-700 ${theme === "dark"
                          ? "dark-mode "
                          : theme === "high-contrast"
                            ? "high-contrast  "
                            : ""
                          }`}
                      >
                        {translations[langMode].longitude}
                      </dt>
                      <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                        {editMode ? (
                          <input
                            type="text"
                            className={`w-full border border-slate-200 focus:border-[#236DB4]
                               focus:ring-2 focus:ring-[#236DB4]/20 rounded-md p-2 ${theme === "dark"
                                ? "dark-mode "
                                : theme === "high-contrast"
                                  ? "high-contrast  "
                                  : ""
                              }`}
                            value={task.longitude}
                            onChange={handleLongitudeChange}
                          />
                        ) : (
                          <span
                            className={`inline-block rounded-md px-3 py-1 text-slate-700 ${theme === "dark"
                              ? "dark-mode "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {task.longitude}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4" title="Geographic region area code for sorting/routing">
                      <dt
                        className={`text-sm font-semibold text-slate-700 ${theme === "dark"
                          ? "dark-mode "
                          : theme === "high-contrast"
                            ? "high-contrast  "
                            : ""
                          }`}
                      >
                        {translations[langMode].areacode}
                      </dt>
                      <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                        {editMode ? (
                          <textarea
                            className={`w-full border border-slate-200 focus:border-[#236DB4]
                               focus:ring-2 focus:ring-[#236DB4]/20 rounded-md p-2 ${theme === "dark"
                                ? "dark-mode "
                                : theme === "high-contrast"
                                  ? "high-contrast  "
                                  : ""
                              }`}
                            value={task.areaCode}
                            onChange={handleAreacodeChange}
                          />
                        ) : (
                          <span
                            className={`inline-block rounded-md px-3 py-1 text-slate-700 ${theme === "dark"
                              ? "dark-mode "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {task.areaCode}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4" title="The billing structure or tariff class applied to this account">
                      <dt
                        className={`text-sm font-semibold text-slate-700 ${theme === "dark"
                          ? "dark-mode "
                          : theme === "high-contrast"
                            ? "high-contrast  "
                            : ""
                          }`}
                      >
                        {translations[langMode].tariff}
                      </dt>
                      <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                        {editMode ? (
                          <textarea
                            className={`w-full border border-slate-200 focus:border-[#236DB4]
                               focus:ring-2 focus:ring-[#236DB4]/20 rounded-md p-2 ${theme === "dark"
                                ? "dark-mode "
                                : theme === "high-contrast"
                                  ? "high-contrast  "
                                  : ""
                              }`}
                            value={task.tariff}
                            onChange={handleTarrifChange}
                          />
                        ) : (
                          <span
                            className={`inline-block rounded-md px-3 py-1 text-slate-700 ${theme === "dark"
                              ? "dark-mode "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {task.tariff}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4" title="The office division responsible for executing this task">
                      <dt
                        className={`text-sm font-semibold text-slate-700 ${theme === "dark"
                          ? "dark-mode "
                          : theme === "high-contrast"
                            ? "high-contrast  "
                            : ""
                          }`}
                      >
                        {translations[langMode].Office}
                      </dt>
                      <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                        {editMode ? (
                          <textarea
                            className={`w-full border border-slate-200 focus:border-[#236DB4]
                               focus:ring-2 focus:ring-[#236DB4]/20 rounded-md p-2 ${theme === "dark"
                                ? "dark-mode "
                                : theme === "high-contrast"
                                  ? "high-contrast  "
                                  : ""
                              }`}
                            value={task.office}
                            onChange={handleOfficeChange}
                          />
                        ) : (
                          <span
                            className={`inline-block rounded-md px-3 py-1 text-slate-700 ${theme === "dark"
                              ? "dark-mode "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {task.office}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4" title="Sub-region or localized area name">
                      <dt
                        className={`text-sm font-semibold text-slate-700 ${theme === "dark"
                          ? "dark-mode "
                          : theme === "high-contrast"
                            ? "high-contrast  "
                            : ""
                          }`}
                      >
                        {translations[langMode].area}
                      </dt>
                      <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                        {editMode ? (
                          <textarea
                            className={`w-full border border-slate-200 focus:border-[#236DB4]
                               focus:ring-2 focus:ring-[#236DB4]/20 rounded-md p-2 ${theme === "dark"
                                ? "dark-mode "
                                : theme === "high-contrast"
                                  ? "high-contrast  "
                                  : ""
                              }`}
                            value={task.area}
                            onChange={handleAreaChange}
                          />
                        ) : (
                          <span
                            className={`inline-block rounded-md px-3 py-1 text-slate-700 ${theme === "dark"
                              ? "dark-mode "
                              : theme === "high-contrast"
                                ? "high-contrast  "
                                : ""
                              }`}
                          >
                            {task.area}
                          </span>
                        )}
                      </dd>
                    </div>
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4" title="Any additional notes or comments left on the task">
                      <dt
                        className={`text-sm font-semibold text-slate-700 ${theme === "dark"
                          ? "dark-mode "
                          : theme === "high-contrast"
                            ? "high-contrast  "
                            : ""
                          }`}
                      >
                        {translations[langMode].remark}
                      </dt>
                      <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                        <span
                          className={`inline-block rounded-md px-3 py-1 text-slate-700 break-words whitespace-pre-wrap ${theme === "dark"
                            ? "dark-mode "
                            : theme === "high-contrast"
                              ? "high-contrast  "
                              : ""
                            }`}
                        >
                          {remarksValue || "-"}
                        </span>
                      </dd>
                    </div>
                    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4 sm:py-4" title="Files, documents, or photos attached to this task">
                      <dt
                        className={`text-sm font-semibold text-slate-700 ${theme === "dark"
                          ? "dark-mode "
                          : theme === "high-contrast"
                            ? "high-contrast  "
                            : ""
                          }`}
                      >
                        {translations[langMode].attachments ?? "Attachments"}
                      </dt>
                      <dd className="mt-1 text-sm sm:col-span-2 sm:mt-0">
                        {getAttachmentUrls().length > 0 ? (
                          <div className="flex flex-wrap gap-3 px-3">
                            {getAttachmentUrls().map((url, i) => {
                              const name = getAttachmentName(url, i);
                              return isImageUrl(url) ? (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={() => handleOpenModal(url)}
                                  title={name}
                                  className="group relative h-20 w-20 overflow-hidden rounded-lg border border-gray-300 shadow-sm transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-400 dark:border-gray-600"
                                >
                                  <img
                                    src={url}
                                    alt={name}
                                    className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                                  />
                                </button>
                              ) : (
                                <a
                                  key={i}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  title={name}
                                  className="flex h-fit max-w-[150px] md:max-w-[200px] items-center gap-2 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700 shadow-sm transition hover:bg-blue-50 hover:shadow-md dark:border-gray-600 dark:bg-[#2d2d2d] dark:text-gray-200 dark:hover:bg-[#3a3a3a]"
                                >
                                  <FaFileAlt className="h-4 w-4 shrink-0 text-blue-500" />
                                  <span className="truncate">{name}</span>
                                  <FaExternalLinkAlt className="h-3 w-3 shrink-0 text-gray-400" />
                                </a>
                              );
                            })}
                          </div>
                        ) : <span
                          className={`inline-block rounded-md px-3 py-1 text-slate-700 break-words whitespace-pre-wrap ${theme === "dark"
                            ? "dark-mode "
                            : theme === "high-contrast"
                              ? "high-contrast  "
                              : ""
                            }`}
                        >
                          -
                        </span>}
                      </dd>
                    </div>
                  </>
                )}
              </dl>
              {editMode && (
                <div className="flex justify-end gap-3 mt-6">
                  <CommonButton colorIntent="danger"
                    variant='danger'
                    tooltipText={translations[langMode].diacardchanges}
                    tooltipPosition="top"
                    onClick={handleDiscard}
                  >
                    {translations[langMode].discard}
                  </CommonButton>
                  <CommonButton colorIntent="success"
                    variant='success'
                    tooltipText={translations[langMode].savethechanges}
                    tooltipPosition="top"
                    onClick={handleConfirm}
                  >
                    {translations[langMode].Confirm}
                  </CommonButton>
                </div>
              )}
            </div>
          </div>
          <div className={`${isSlider ? "lg:w-1/2" : "w-full"} w-full`}>
            <div className=" py-2 flex justify-between items-center">
              <h3
                className={`text-sm font-semibold ${theme === "dark"
                  ? "dark-mode text-white"
                  : theme === "high-contrast"
                    ? "high-contrast text-black"
                    : "text-slate-700"
                  }`}
              >
                {translations[langMode].taskhistory}
              </h3>
              <span className="inline-flex h-7 w-7 invisible" aria-hidden="true" />
            </div>
            <div
              id="accordion"
              className="custom-scrollbar rounded-xl p-3 bg-white dark:bg-[#1f1f1f] border border-slate-200"
              style={{ height: "1100px", overflowY: "scroll" }}
              onScroll={handleScroll}
            >
              {taskHistory.length === 0 ? (
                !isLoading && (
                  <div className="text-center py-10 text-slate-400 text-sm">
                    {translations[langMode].nohistory}
                  </div>
                )
              ) : (
                taskHistory.map((task, index) => {
                  const parsedBody = parseBodyObject(task.body);
                  return (
                  <div key={index} className="mb-2.5 mx-1">
                    <div
                      className={`cursor-pointer bg-slate-50/60 border border-slate-100 p-3.5 rounded-lg transition duration-200 hover:bg-slate-100/60 flex justify-between items-center ${theme === "dark"
                        ? "dark-mode bg-[#2d2d2d] border-gray-600 hover:bg-[#3a3a3a]"
                        : theme === "high-contrast"
                          ? "high-contrast"
                          : ""
                        }`}
                      onClick={() => handleToggle(index)}
                      title="Click to expand/collapse details of this historical action"
                    >
                      <h5
                        className={`text-base font-medium ${theme === "dark"
                          ? "dark-mode text-white"
                          : theme === "high-contrast"
                            ? "high-contrast"
                            : "text-slate-800"
                          }`}
                      >
                        user {task.userId} - {task.heading}
                      </h5>
                      <div className="flex items-center gap-2">
                        <h5
                          className={`text-sm ${theme === "dark"
                            ? "dark-mode text-gray-300"
                            : theme === "high-contrast"
                              ? "high-contrast"
                              : "text-slate-500"
                            }`}
                        >
                          {formatDate(task.createdAt)}
                        </h5>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`w-5 h-5 transform transition-transform duration-300 ${expandedTask === index ? "rotate-180" : "rotate-0"
                            }`}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>

                    <div
                      className={`overflow-hidden transition-all duration-500 ease-in-out ${expandedTask === index ? "max-h-screen mt-2" : "max-h-0"
                        }`}
                    >
                      <div
                        className={`p-4 rounded-md bg-slate-50 border border-slate-200 shadow-inner ${theme === "dark"
                          ? "dark-mode bg-[#2b2b2b] border-gray-600"
                          : theme === "high-contrast"
                            ? "high-contrast"
                            : ""
                          }`}
                      >
                        <div className="flex gap-4 items-center">
                          {isValidURL(task.body) ? (
                            <img
                              src={task.body}
                              alt="Task"
                              className="max-w-full h-44 object-cover rounded-md border border-slate-200 shadow-sm hover:cursor-pointer"
                              onClick={() => handleOpenModal(task.body)}
                              title="Click to view image"
                            />
                          ) : parsedBody ? (
                            <div className="w-full overflow-x-auto">
                              <table
                                className={`w-full text-sm border border-slate-200 rounded-md overflow-hidden ${theme === "dark"
                                  ? "dark-mode border-gray-600"
                                  : theme === "high-contrast"
                                    ? "high-contrast"
                                    : ""
                                  }`}
                              >
                                <tbody>
                                  {parsedBody.map(({ key, value }) => (
                                    <tr
                                      key={key}
                                      className={`border-b border-slate-200 last:border-b-0 ${theme === "dark"
                                        ? "dark-mode border-gray-600"
                                        : theme === "high-contrast"
                                          ? "high-contrast"
                                          : ""
                                        }`}
                                    >
                                      <th
                                        scope="row"
                                        className={`text-left font-semibold px-3 py-2 align-top whitespace-nowrap ${theme === "dark"
                                          ? "dark-mode text-gray-200 bg-[#333]"
                                          : theme === "high-contrast"
                                            ? "high-contrast"
                                            : "text-slate-700 bg-slate-100"
                                          }`}
                                      >
                                        {humanizeKey(key)}
                                      </th>
                                      <td
                                        className={`px-3 py-2 align-top break-words ${theme === "dark"
                                          ? "dark-mode text-gray-200"
                                          : theme === "high-contrast"
                                            ? "high-contrast"
                                            : "text-slate-700"
                                          }`}
                                      >
                                        {value === "" ? "-" : value}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p
                              className={`text-sm leading-relaxed ${theme === "dark"
                                ? "dark-mode text-gray-200"
                                : theme === "high-contrast"
                                  ? "high-contrast"
                                  : "text-slate-700"
                                }`}
                            >
                              {task.body}
                            </p>
                          )}
                        </div>
                        <div
                          className={`mt-4 text-sm italic ${theme === "dark"
                            ? "dark-mode text-gray-400"
                            : theme === "high-contrast"
                              ? "high-contrast"
                              : "text-slate-500"
                            }`}
                        >
                          {task.comment}
                        </div>
                        {attachmentEntryIndexes.has(index) && (
                          <div className="mt-4">
                            <p
                              className={`mb-2 text-xs font-semibold uppercase tracking-wide ${theme === "dark"
                                ? "dark-mode text-gray-300"
                                : theme === "high-contrast"
                                  ? "high-contrast"
                                  : "text-slate-500"
                                }`}
                            >
                              {translations[langMode]?.attachments ?? "Attachments"}
                            </p>
                            <AttachmentList
                              attachments={task.attachments}
                              resolveFresh={resolveFreshHistoryAttachment(task)}
                              theme={theme}
                              langMode={langMode}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })
              )}

              {isLoading && (
                <div className="text-center py-4 text-sm text-[#236DB4]">
                  {translations[langMode].more}
                </div>
              )}

            </div>
          </div>

        </div >
      </div >
      {open && <Modal open={open} setOpen={setOpen} img={img} />}
    </div >
  );
};
export default TaskDetailPage;
